import React from "react";
import { Container, Icon } from "semantic-ui-react";
import { PAGE_SIZE } from "../constants";
import { NAV } from "../navLabels";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useGetAllowancesQuery } from "../features/chat_sessions/allowancesApiSlice";
import { useGetChatSessionsQuery } from "../features/chat_sessions/chatSessionApiSlice";
import { customerLabel, minutesLabel, shortDate } from "../features/chat_sessions/MobileChatSessionList";
import { SectionTitle, cardStyle, scoreColor } from "./reportCards";

/**
 * The trainee's home (site root). Same header band, gutter and cards as every other trainee
 * page (docs/FRONTEND_UI.md): one primary card into 롤플레이 with what's left of the quota,
 * the latest sessions, and the other sections as a list.
 */

const pill = (primary) => ({
  border: "none",
  borderRadius: "999px",
  padding: "0 22px",
  height: "44px",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  backgroundColor: primary ? "white" : "rgba(255,255,255,0.18)",
  color: primary ? "#007AFF" : "white",
});

const iconChip = (size, bg, color) => ({
  width: size,
  height: size,
  flexShrink: 0,
  borderRadius: "50%",
  backgroundColor: bg,
  color,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

/** Remaining practice minutes / graded attempts; nothing for exempt users (admins). */
const QuotaChips = ({ allowances }) => {
  const chips = [];
  if (allowances?.graded) chips.push(`평가 응시 ${allowances.graded.attempts_remaining}회 남음`);
  if (allowances?.practice) chips.push(`사전 연습 ${allowances.practice.minutes_remaining}분 남음`);
  if (!chips.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "14px" }}>
      {chips.map((c) => (
        <span key={c} style={{ fontSize: "12px", fontWeight: 600, padding: "4px 10px", borderRadius: "999px", backgroundColor: "rgba(255,255,255,0.18)" }}>
          {c}
        </span>
      ))}
    </div>
  );
};

const RecentSessions = ({ navigate }) => {
  const { data } = useGetChatSessionsQuery({ page: 1, page_size: 3, has_lines: 1 }, { refetchOnMountOrArgChange: 60 });
  const sessions = data?.results || [];
  if (!sessions.length) return null;
  const listUrl = `/chatsessionslist?page=1&page_size=${PAGE_SIZE}`;

  return (
    <>
      <SectionTitle meta={<span role="button" onClick={() => navigate(listUrl)} style={{ color: "#007AFF", cursor: "pointer" }}>전체 보기 ›</span>}>
        최근 롤플레이
      </SectionTitle>
      <div style={{ ...cardStyle, padding: "4px 16px" }}>
        {sessions.map((s, i) => {
          const evaluated = s.evaluation_session_count > 0;
          // 응대 스킬 점수 on 100, as every list shows it (raw points differ by criteria set).
          const score = s.latest_evaluation_percent;
          return (
            <div
              key={s.id}
              role="button"
              // Not yet evaluated: the list is where it can be resumed or sent for grading.
              onClick={() => navigate(evaluated ? `/chatsessions/${s.id}` : listUrl)}
              style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", cursor: "pointer", borderTop: i ? "1px solid rgba(0,0,0,0.05)" : "none" }}
            >
              <span style={{ fontSize: "12px", fontWeight: 600, padding: "3px 8px", borderRadius: "999px", flexShrink: 0, color: s.is_practice ? "#6e6e73" : "#007AFF", backgroundColor: s.is_practice ? "#F2F2F7" : "#EAF3FF" }}>
                {s.is_practice ? "연습" : "평가"}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: "15px", fontWeight: 600, color: "#1d1d1f" }}>{customerLabel(s.persona)}</div>
                <div style={{ fontSize: "12px", color: "#86868b" }}>
                  {[shortDate(s.created_at), minutesLabel(s.duration)].filter(Boolean).join(" · ")}
                </div>
              </div>
              <span style={{ fontSize: "15px", fontWeight: 700, color: score != null ? scoreColor(score) : "#86868b" }}>
                {score != null ? `${Math.round(score)}점` : evaluated ? "채점 중" : "미평가"}
              </span>
              <Icon name="angle right" style={{ margin: 0, color: "#c7c7cc" }} />
            </div>
          );
        })}
      </div>
    </>
  );
};

const UserHome = ({ user, navigate }) => {
  const { isMobile } = useBreakpoint();
  const { data: allowances } = useGetAllowancesQuery();
  const name = user?.full_name || user?.username || "사용자";

  const links = [
    { icon: "dashboard", label: NAV.DASHBOARD, sub: "점수 추이와 영역별 강점·보완점", to: "/userdashboard" },
    { icon: "history", label: NAV.SESSIONS, sub: "지난 롤플레이와 평가 결과", to: `/chatsessionslist?page=1&page_size=${PAGE_SIZE}` },
    user?.contractor?.knowledge_training_enabled !== false && { icon: "student", label: NAV.QUIZ, sub: "퀴즈로 제품 지식 강화", to: "/quiz/dashboard" },
  ].filter(Boolean);

  return (
    <div style={{ backgroundColor: "#f9fafb", minHeight: isMobile ? "calc(100dvh - 50px)" : "calc(100vh - 50px)", paddingBottom: "32px" }}>
      <div
        className="page-header-band"
        style={{ padding: "16px 0", backgroundColor: "rgba(255,255,255,0.9)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(0,0,0,0.05)" }}
      >
        <Container>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div className="header-icon-desktop-only" style={iconChip("40px", "#F2F2F7", "#007AFF")}>
              <Icon name="home" className="centered-icon" style={{ fontSize: "18px" }} />
            </div>
            <div>
              <h2 style={{ fontSize: "17px", fontWeight: 600, margin: 0, color: "#1d1d1f" }}>{name}님, 환영합니다</h2>
              <p style={{ fontSize: "13px", color: "#86868b", margin: 0 }}>오늘도 고객 응대 스킬을 향상시켜 볼까요?</p>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "3fr 2fr", gap: isMobile ? "12px" : "16px", alignItems: "stretch" }}>
          <div
            role="button"
            onClick={() => navigate("/simulation")}
            style={{ ...cardStyle, border: "none", borderRadius: isMobile ? "20px" : "24px", padding: isMobile ? "20px" : "28px", background: "linear-gradient(135deg, #007AFF, #0055D4)", color: "white", cursor: "pointer", display: "flex", flexDirection: "column" }}
          >
            <div style={iconChip("44px", "rgba(255,255,255,0.18)", "white")}>
              <Icon name="conversation" className="centered-icon" style={{ fontSize: "20px" }} />
            </div>
            <div style={{ fontSize: isMobile ? "20px" : "24px", fontWeight: 700, marginTop: "14px" }}>{NAV.ROLEPLAY}</div>
            <div style={{ fontSize: "14px", lineHeight: 1.5, marginTop: "4px", color: "rgba(255,255,255,0.88)", wordBreak: "keep-all" }}>
              AI 고객과 대화하며 응대를 연습하고, 준비가 되면 평가에 응시하세요.
            </div>
            <QuotaChips allowances={allowances} />
            <div style={{ flex: 1 }} />
            <div style={{ marginTop: "18px" }}>
              <button type="button" style={pill(true)}>
                시작하기 <Icon name="arrow right" style={{ margin: "0 0 0 6px" }} />
              </button>
            </div>
          </div>

          <div style={{ ...cardStyle, padding: "4px 16px", display: "flex", flexDirection: "column", justifyContent: "space-around" }}>
            {links.map((l, i) => (
              <div
                key={l.to}
                role="button"
                onClick={() => navigate(l.to)}
                style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 0", cursor: "pointer", borderTop: i ? "1px solid rgba(0,0,0,0.05)" : "none" }}
              >
                <div style={iconChip("36px", "#EAF3FF", "#007AFF")}>
                  <Icon name={l.icon} className="centered-icon" style={{ fontSize: "16px" }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: "15px", fontWeight: 600, color: "#1d1d1f" }}>{l.label}</div>
                  <div style={{ fontSize: "12px", color: "#86868b" }}>{l.sub}</div>
                </div>
                <Icon name="angle right" style={{ margin: 0, color: "#c7c7cc" }} />
              </div>
            ))}
          </div>
        </div>

        <RecentSessions navigate={navigate} />
      </Container>
    </div>
  );
};

export default UserHome;
